import { Link } from "react-router-dom";
import {
  ArrowRight, Heart, Home as HomeIcon, Users, Sparkles, ShieldCheck,
  Compass, HandHeart, CheckCircle2, Star, PhoneCall, MessageSquare, Map,
} from "lucide-react";
import { useSeo } from "../lib/seo";
import { LOGO_URL } from "../components/Logo";

const services = [
  { icon: HomeIcon, title: "Supported Independent Living", desc: "24/7 in-home support so you can live independently in a place you call your own.", href: "/services#sil" },
  { icon: HandHeart, title: "In-Home Support", desc: "Personal care and daily living support delivered with warmth in your own home.", href: "/services#inhome" },
  { icon: Users, title: "Community Access", desc: "Get out, get involved — social, recreational and skill-building outings.", href: "/services#community" },
  { icon: Sparkles, title: "Capacity Building", desc: "Practical life skills support, helping you work toward the goals in your plan.", href: "/services#capacity" },
  { icon: Map, title: "Support Coordination", desc: "We help you understand your plan, connect with providers, and make every dollar count.", href: "/services#coordination" },
];

const whyUs = [
  { icon: ShieldCheck, title: "Safe & supportive", desc: "Trained staff, clear safeguards, and a culture that treats every participant as family." },
  { icon: Compass, title: "Goal-focused", desc: "Every support links back to the goals in your NDIS plan — measurable progress, no fluff." },
  { icon: Heart, title: "Person-centred", desc: "You choose how, when, and with whom. We listen first, then build supports around you." },
  { icon: CheckCircle2, title: "Experienced team", desc: "Skilled support workers with cultural awareness and lived disability experience." },
];

const values = ["Compassion", "Respect", "Inclusion", "Empowerment", "Independence"];

const steps = [
  { n: "01", t: "Get in touch", d: "Send an enquiry or call us. We'll have a no-pressure chat about what you're looking for." },
  { n: "02", t: "Discuss your goals", d: "We meet (in person or online), understand your plan, your routine and what success looks like." },
  { n: "03", t: "Agree on supports", d: "We co-design a simple service agreement — clear hours, clear costs, your choice of workers." },
  { n: "04", t: "Start your supports", d: "We match you with the right team and begin. You stay in control the whole way through." },
];

const testimonials = [
  { quote: "Valora actually listened. The team felt like family from day one, and my brother has more good days than not now.", author: "Sibling · Gold Coast" },
  { quote: "I've grown so much in the last six months — cooking, catching trains, even job interviews. They believed in me.", author: "Participant · Brisbane" },
  { quote: "As a support coordinator I look for providers who follow through. Valora does, every single time.", author: "Support Coordinator · Sydney" },
];

export default function Home() {
  useSeo({
    title: "Valora Support Services | NDIS Supported Independent Living Australia",
    description: "Warm, person-centred NDIS Supported Independent Living (SIL) and daily supports across Australia — in-home support, community access and capacity building. Make an enquiry today.",
  });

  return (
    <div data-testid="home-page">
      {/* BRAND BANNER */}
      <section data-testid="brand-banner" className="relative overflow-hidden bg-gradient-to-br from-[#0F2A2C] via-[#0B6E70] to-[#45A29E]">
        <div aria-hidden className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(60% 70% at 85% 30%, #7FD1CB 0%, transparent 60%), radial-gradient(45% 60% at 10% 80%, #B5A3D1 0%, transparent 60%)" }} />
        <div aria-hidden className="absolute inset-0 grain opacity-20" />
        <div className="container-x relative py-12 lg:py-16 grid lg:grid-cols-12 items-center gap-8">
          <div className="lg:col-span-7 text-white">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold tracking-[0.18em] uppercase text-[#E6FFFA] border border-white/15">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7FD1CB] animate-soft-pulse" /> NDIS Supports · Servicing Australia
            </span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.1]">
              Rise. Grow. <em className="text-[#7FD1CB] not-italic">Live the life you choose.</em>
            </h2>
            <p className="mt-4 text-white/85 max-w-xl text-lg">
              Valora Support Services — Supported Independent Living and core NDIS supports delivered with warmth, respect and real choice.
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <div className="bg-white/95 backdrop-blur rounded-3xl px-8 py-6 lg:px-10 lg:py-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.45)]">
              <img
                src={LOGO_URL}
                alt="Valora Support Services logo"
                className="h-36 sm:h-44 lg:h-52 w-auto block mx-auto"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="absolute inset-0 grain opacity-50" aria-hidden />
        <div aria-hidden className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[#7FD1CB]/30 blur-3xl" />
        <div aria-hidden className="absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-[#B5A3D1]/25 blur-3xl" />

        <div className="container-x relative pt-14 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="pill" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-[#0B6E70]" /> NDIS Supports · Servicing Australia
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#0F2A2C]">
              NDIS Supported Independent Living and{" "}
              <span className="relative inline-block">
                <span className="relative z-10 italic font-semibold bg-gradient-to-r from-[#0B6E70] via-[#45A29E] to-[#827397] bg-clip-text text-transparent">
                  flexible support
                </span>
              </span>{" "}
              that puts you first.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[#4A5568] leading-relaxed">
              Valora supports NDIS participants, families, and carers right across Australia with Supported Independent Living (SIL), in-home support, community access and capacity building — delivered with warmth, dignity, and genuine choice.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
              <Link to="/referrals" data-testid="hero-cta-enquire" className="btn-primary">
                Make an enquiry <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="btn-secondary">
                Explore our supports
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-[#4A5568]">
              <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#0B6E70]" /> 4.9 average family rating</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#0B6E70]" /> NDIS-aligned safeguards</div>
              <div className="flex items-center gap-2"><HandHeart className="h-4 w-4 text-[#0B6E70]" /> Available 7 days</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden shadow-[0_40px_80px_-30px_rgba(11,110,112,0.45)] ring-1 ring-white">
              <img
                src="https://images.unsplash.com/photo-1570793005299-c091be91bbad"
                alt="Person in a wheelchair enjoying nature, smiling and relaxed"
                className="h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-x-4 bottom-4 rounded-2xl bg-white/95 backdrop-blur p-4 flex items-center gap-3 shadow-lg">
                <div className="h-10 w-10 rounded-full bg-[#E6FFFA] flex items-center justify-center">
                  <PhoneCall className="h-5 w-5 text-[#0B6E70]" />
                </div>
                <div className="text-sm">
                  <div className="font-semibold text-[#0F2A2C]">Talk to a real person</div>
                  <div className="text-[#4A5568]">Mon–Sat · 8am–6pm AEST</div>
                </div>
              </div>
            </div>
            <div className="absolute -left-6 -bottom-6 hidden md:block rounded-2xl bg-white border border-[#e6efee] p-4 shadow-lg max-w-[220px]">
              <div className="text-xs uppercase tracking-wider text-[#827397] font-semibold">Service area</div>
              <p className="mt-1.5 text-sm text-[#0F2A2C] font-medium">Proudly servicing all of Australia</p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE OVERVIEW */}
      <section className="section-y bg-white" data-testid="home-services">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
            <div className="lg:col-span-7">
              <p className="eyebrow">Our supports</p>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#0F2A2C] tracking-tight">
                Everyday supports, designed around <em className="text-[#0B6E70] not-italic font-semibold">your</em> goals.
              </h2>
            </div>
            <div className="lg:col-span-5 text-[#4A5568] text-lg">
              Whether you need a short break, daily help at home, more time in the community, or skills for independence — we have you covered.
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {services.map((s, i) => (
              <Link key={s.title} to={s.href} data-testid={`service-card-${i}`} className="card-soft p-7 group">
                <div className="h-12 w-12 rounded-xl bg-[#E6FFFA] text-[#0B6E70] flex items-center justify-center mb-5 group-hover:bg-[#0B6E70] group-hover:text-white transition-colors">
                  <s.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-[#0F2A2C]">{s.title}</h3>
                <p className="mt-2 text-[#4A5568] text-[15px] leading-relaxed">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B6E70] group-hover:gap-2.5 transition-all">
                  Learn more <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-y bg-[#F8FBFA]" data-testid="home-why">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="rounded-[28px] overflow-hidden aspect-[4/5] shadow-[0_40px_80px_-40px_rgba(11,110,112,0.5)]">
              <img
                src="https://customer-assets.emergentagent.com/job_valora-respite/artifacts/7dz1vjrx_image.png"
                alt="Young man and his support worker smiling while playing a board game at home"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -right-4 -bottom-4 lg:right-6 bg-white border border-[#e6efee] rounded-2xl p-5 shadow-lg max-w-[240px]">
              <div className="font-serif text-3xl text-[#0B6E70] font-semibold">98%</div>
              <p className="mt-1 text-sm text-[#4A5568]">of participants say they'd recommend Valora to a friend or family member.</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow">Why families choose us</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#0F2A2C] tracking-tight">
              Support that respects the whole person.
            </h2>
            <p className="mt-5 text-[#4A5568] text-lg leading-relaxed">
              We hire for character first, then train for skill. That means a calmer environment, more consistent workers, and supports that genuinely move your goals forward.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {whyUs.map((w) => (
                <div key={w.title} className="bg-white rounded-2xl p-5 border border-[#e6efee]">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#E6FFFA] text-[#0B6E70] flex items-center justify-center">
                      <w.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-[#0F2A2C]">{w.title}</h3>
                  </div>
                  <p className="mt-3 text-[#4A5568] text-sm leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VALUES */}
      <section className="section-y bg-white relative overflow-hidden" data-testid="home-values">
        <div aria-hidden className="absolute -right-32 top-20 h-[380px] w-[380px] rounded-full bg-[#B5A3D1]/15 blur-3xl" />
        <div className="container-x relative">
          <div className="max-w-3xl">
            <p className="eyebrow">Our mission</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium text-[#0F2A2C] tracking-tight">
              To walk beside every participant with care, respect, and understanding — empowering them to achieve their goals, embrace new opportunities, and live the life they choose.
            </h2>
            <p className="mt-6 text-[#4A5568] text-lg leading-relaxed">
              Five values shape how we show up every day. They are not posters on a wall — they are how we hire, train, and measure ourselves.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-5 gap-4">
            {values.map((v, i) => (
              <div
                key={v}
                className="rounded-2xl p-6 text-center border border-[#e6efee] bg-gradient-to-b from-white to-[#F8FBFA] hover:from-[#E6FFFA] hover:to-white transition-colors"
                data-testid={`value-card-${i}`}
              >
                <div className="font-serif text-2xl font-semibold text-[#0B6E70]">0{i + 1}</div>
                <div className="mt-2 font-serif text-xl text-[#0F2A2C] font-semibold">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW TO GET STARTED */}
      <section className="section-y bg-[#0F2A2C] text-white relative overflow-hidden" data-testid="home-steps">
        <div aria-hidden className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(60% 50% at 80% 20%, #45A29E 0%, transparent 60%), radial-gradient(50% 40% at 10% 90%, #827397 0%, transparent 60%)" }} />
        <div className="container-x relative">
          <div className="max-w-2xl">
            <p className="eyebrow text-[#7FD1CB]">How to get started</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-serif font-medium tracking-tight">
              Four simple steps — at your pace.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s) => (
              <div key={s.n} className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 p-6 hover:bg-white/10 transition-colors">
                <div className="font-serif text-4xl text-[#7FD1CB] font-semibold">{s.n}</div>
                <h3 className="mt-3 font-serif text-xl font-semibold">{s.t}</h3>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-col sm:flex-row gap-3">
            <Link to="/referrals" data-testid="steps-cta" className="inline-flex items-center gap-2 rounded-full bg-white text-[#0F2A2C] px-6 py-3 font-semibold hover:bg-[#E6FFFA]">
              Start an enquiry <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:0426635885" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 font-semibold hover:bg-white/10">
              <PhoneCall className="h-4 w-4" /> Call 0426 635 885
            </a>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS section removed — no reviews yet */}

      {/* CTA STRIP */}
      <section className="bg-white">
        <div className="container-x py-16 lg:py-20">
          <div className="rounded-[28px] bg-gradient-to-br from-[#0B6E70] via-[#0B6E70] to-[#45A29E] p-10 lg:p-14 text-white grid lg:grid-cols-12 gap-8 items-center relative overflow-hidden">
            <div aria-hidden className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-white/10" />
            <div aria-hidden className="absolute -left-10 -bottom-10 h-60 w-60 rounded-full bg-[#827397]/30 blur-2xl" />
            <div className="lg:col-span-8 relative">
              <h3 className="font-serif text-3xl lg:text-4xl font-medium tracking-tight">Not sure where to start? <span className="italic text-[#E6FFFA]">We'll guide you.</span></h3>
              <p className="mt-3 text-white/85 max-w-2xl">Whether you're brand new to the NDIS or switching providers, our intake team makes it simple. One quick form, one friendly call.</p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:justify-end gap-3 relative">
              <Link to="/referrals" data-testid="bottom-cta-enquire" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#0B6E70] px-6 py-3 font-semibold hover:bg-[#E6FFFA]">
                Enquire now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" data-testid="bottom-cta-contact" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 px-6 py-3 font-semibold hover:bg-white/10">
                <MessageSquare className="h-4 w-4" /> Contact
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
